import { z } from "zod";

import {
  CanonicalDataType,
  arrayDefaultsToPlot,
  type MessagePathRecord,
} from "@/shared/domain/topics";

import { fileSchema } from "./file";
import { logPanelConfigSchema } from "./log-panel";
import { threeDPanelConfigSchema, threeDPanelDataSchema } from "./three-d";
import { topicDataSchema } from "./topic-data";

export type { File } from "./file";

const datasetSchema = z.strictObject({
  datasetId: z.string(),
});

export type Dataset = z.infer<typeof datasetSchema>;

const sessionSchema = z.strictObject({
  sessionId: z.string(),
});

/**
 * A datasource the workspace loads. `session` is a first-class datasource, on a
 * par with `dataset`: the route turns each `?session=` into a session reference
 * (no fetching), and `WorkspaceDataService` expands it to the session's files
 * and record — so the grouping survives save/share, not just the URL.
 */
export const datasourceSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("file"), value: fileSchema }),
  z.strictObject({ type: z.literal("dataset"), value: datasetSchema }),
  z.strictObject({ type: z.literal("session"), value: sessionSchema }),
]);

export type Datasource = z.infer<typeof datasourceSchema>;
export type DatasetSource = Extract<Datasource, { type: "dataset" }>;
export type FileSource = Extract<Datasource, { type: "file" }>;
export type SessionSource = Extract<Datasource, { type: "session" }>;

export enum PanelType {
  Document = "Document",
  Image = "Image",
  Log = "Log",
  Map = "Map",
  Plot = "Plot",
  RawMessage = "RawMessage",
  ThreeD = "ThreeD",
}

export enum LayoutType {
  Panel = "Panel",
}

//// IMAGE PANEL
const imageClipSchema = z.object({
  data: topicDataSchema,
  id: z.string(),
});
export type ImagePanelClip = z.infer<typeof imageClipSchema>;

const imagePanelConfigSchema = z.object({
  rotation: z.number(),
  stretchToFitCanvas: z.boolean(),
});

export type ImagePanelConfig = z.infer<typeof imagePanelConfigSchema>;

export function isImagePanelState(state: PanelState): state is ImagePanelState {
  return state.type === PanelType.Image;
}

//// MAP PANEL
const coordinateUnitSchema = z.enum(["deg_float", "deg_int_1e7"] as const);
export type CoordinateUnit = z.infer<typeof coordinateUnitSchema>;

const mapPathSchema = z.object({
  data: z.array(topicDataSchema),
  id: z.string(),
  coordinateUnits: z
    .object({
      lat: coordinateUnitSchema.optional(),
      lon: coordinateUnitSchema.optional(),
    })
    .optional(),
  style: z.object({
    lineColor: z.string().optional(),
    label: z.string().optional(),
  }),
  visible: z.boolean(),
});

export type MapPath = z.infer<typeof mapPathSchema>;

const mapPanelInitParamsSchema = z.strictObject({
  viewport: z
    .object({
      center: z.object({ lng: z.number(), lat: z.number() }),
      zoom: z.number(),
      bearing: z.number().optional(),
      pitch: z.number().optional(),
    })
    .optional(),
});

export type MapPanelInitParams = z.infer<typeof mapPanelInitParamsSchema>;

export function isMapPanelState(state: PanelState): state is MapPanelState {
  return state.type === PanelType.Map;
}

//// PLOT PANEL
export const plotYAxisSchema = z.union([
  z.literal("y"),
  z.templateLiteral(["y", z.number(), z.enum(["l", "r"])]),
]);

export type PlotYAxis = z.infer<typeof plotYAxisSchema>;

export const formulaTransformSchema = z.object({
  type: z.literal("formula"),
  expression: z.string(),
});

/**
 * A math transform applied to a series' Y values. Point-wise expressions are
 * evaluated with `y` as each value; windowed functions (`savgol`, `deriv`)
 * evaluate over the whole series. See `@/shared/math/expression-evaluator` for
 * the grammar and supported functions.
 */
export type FormulaTransform = z.infer<typeof formulaTransformSchema>;

const plotSeriesConfigSchema = z.object({
  lineColor: z.string(),
  lineWidth: z.number().optional(),
  pointRadius: z.number().optional(),
  yAxis: plotYAxisSchema.optional(),
  breakLineAtDiscontinuity: z.boolean().optional(),
  label: z.string().optional(),
  transform: formulaTransformSchema.optional(),
});

export type PlotSeriesConfig = z.infer<typeof plotSeriesConfigSchema>;

const plotSeriesSchema = z.object({
  config: plotSeriesConfigSchema.optional(),
  data: topicDataSchema,
  id: z.string(),
  visible: z.boolean(),
});

export type PlotSeries = z.infer<typeof plotSeriesSchema>;

/**
 * Which axes of the viewport of a particular panel to sync with other panels.
 *
 * "xy" - sync both axes
 * "x" - sync the x axis
 * "y" - sync the y axis
 * "" - sync nothing
 */
const viewportSyncModeSchema = z.enum(["xy", "x", "y", ""] as const);
export type ViewportSyncMode = z.infer<typeof viewportSyncModeSchema>;

const plotPanelInitParamsSchema = z.strictObject({
  viewport: z
    .object({
      x: z.object({ min: z.number(), max: z.number() }),
      y: z.object({ min: z.number(), max: z.number() }),
    })
    .optional(),
});

export type PlotPanelInitParams = z.infer<typeof plotPanelInitParamsSchema>;

export function isPlotPanelState(state: PanelState): state is PlotPanelState {
  return state.type === PanelType.Plot;
}

export enum LegendPosition {
  TopLeft = "TopLeft",
  TopRight = "TopRight",
  BottomLeft = "BottomLeft",
  BottomRight = "BottomRight",
}

export enum TimeBoundsSource {
  FilesInWorkspace = "FilesInWorkspace",
  FilesInPanel = "FilesInPanel",
  TopicsInPanel = "TopicsInPanel",
}

const plotAxisConfigSchema = z.object({
  label: z.string().optional(),
  bounds: z
    .object({
      min: z.number(),
      max: z.number(),
      locked: z.boolean().optional(),
      setBy: z.enum(["settings", "chart"] as const).optional(),
    })
    .optional(),
});

export type PlotAxisConfig = z.infer<typeof plotAxisConfigSchema>;

/**
 * Where x = 0 sits for each source when a plot panel aligns sessions:
 *
 * - `"session-start"`: the start of the series' source (its recording
 *   session, or its file when it arrived outside a session).
 * - `"first-data"`: the earliest start inside the source's span among all of
 *   the source's topics, plotted or not.
 * - `{ eventName }`: the start of the earliest event with that name in the
 *   series' source; a source without the event falls back to its start.
 */
const sessionAlignmentAnchorSchema = z.union([
  z.enum(["session-start", "first-data"] as const),
  z.object({ eventName: z.string().min(1) }),
]);

export type SessionAlignmentAnchor = z.infer<
  typeof sessionAlignmentAnchorSchema
>;

/**
 * How an aligned plot panel maps its series onto elapsed time: `unit` names
 * what shares one x = 0, and `anchor` says where that zero sits within a
 * session.
 *
 * - `{ unit: "session", anchor }`: every series of one recording session
 *   shares the zero the anchor picks, so sessions recorded at different times
 *   overlay.
 * - `{ unit: "series" }`: each series takes its own topic's start as zero, so
 *   every curve begins at x = 0 whatever session it came from.
 */
const xAxisAlignmentSchema = z.discriminatedUnion("unit", [
  z.object({
    unit: z.literal("session"),
    anchor: sessionAlignmentAnchorSchema,
  }),
  z.object({ unit: z.literal("series") }),
]);

export type XAxisAlignment = z.infer<typeof xAxisAlignmentSchema>;

const plotPanelConfigSchema = z.object({
  timeBoundsSource: z.enum(TimeBoundsSource).optional(),
  showEvents: z.boolean().optional(),
  /** Absent = drawn, per `DEFAULT_PLOT_PANEL_CONFIG`. */
  showPlayhead: z.boolean().optional(),
  viewportSyncMode: viewportSyncModeSchema.optional(),
  /**
   * Absent = absolute time: every series shares one panel-wide basis.
   * Present = aligned: each series plots elapsed time from its unit's basis,
   * overlaying data recorded at different times.
   *
   * An alignment this schema rejects parses as absent, so the panel falls back
   * to absolute time instead of the whole workspace failing to open. That
   * covers a unit or anchor kind a newer client wrote, an event anchor with an
   * empty name, and a persisted shape carrying no unit.
   */
  xAxisAlignment: xAxisAlignmentSchema.optional().catch(undefined),
  xAxis: plotAxisConfigSchema.optional(),
  yAxis: plotAxisConfigSchema.optional(),
  extraYAxes: z.record(z.string(), plotAxisConfigSchema).optional(),
  legendPosition: z.enum(LegendPosition).optional(),
});

export type PlotPanelConfig = z.infer<typeof plotPanelConfigSchema>;

//// 3D PANEL
export type {
  PanelData as ThreeDPanelData,
  PanelConfig as ThreeDPanelConfig,
  Entity as ThreeDEntity,
  EntityRegistry as ThreeDEntityRegistry,
  Joint as ThreeDJoint,
  JointValue as ThreeDJointValue,
  ThirdPersonCamera as ThreeDThirdPersonCamera,
} from "./three-d";

export function isThreeDPanelState(
  state: PanelState,
): state is ThreeDPanelState {
  return state.type === PanelType.ThreeD;
}

//// RAW MESSAGE PANEL
export function isRawMessagePanelState(
  state: PanelState,
): state is RawMessagePanelState {
  return state.type === PanelType.RawMessage;
}

//// DOCUMENT PANEL
const documentPanelDataSchema = z.object({
  fileId: z.string(),
  /**
   * Full relative path of the file (e.g. "config/params.yaml"), not just
   * the basename. Used for extension-based type detection and as the
   * panel title via the reducer.
   */
  fileName: z.string(),
});

export type DocumentPanelData = z.infer<typeof documentPanelDataSchema>;

export function isDocumentPanelState(
  state: PanelState,
): state is DocumentPanelState {
  return state.type === PanelType.Document;
}

//// LOG PANEL

export type { LogPanelConfig } from "./log-panel";

export function isLogPanelState(state: PanelState): state is LogPanelState {
  return state.type === PanelType.Log;
}

export type PanelStateByType<T extends PanelType = PanelType> = {
  [PanelType.Document]: DocumentPanelState;
  [PanelType.Image]: ImagePanelState;
  [PanelType.Log]: LogPanelState;
  [PanelType.Map]: MapPanelState;
  [PanelType.Plot]: PlotPanelState;
  [PanelType.RawMessage]: RawMessagePanelState;
  [PanelType.ThreeD]: ThreeDPanelState;
}[T];

export type PanelStates =
  | DocumentPanelState
  | ImagePanelState
  | LogPanelState
  | MapPanelState
  | PlotPanelState
  | RawMessagePanelState
  | ThreeDPanelState;

const panelSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(PanelType.Plot),
    config: plotPanelConfigSchema.optional(),
    data: z.array(plotSeriesSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: plotPanelInitParamsSchema.optional(),
  }),
  z.object({
    type: z.literal(PanelType.ThreeD),
    config: threeDPanelConfigSchema.optional(),
    data: threeDPanelDataSchema,
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Map),
    config: z.unknown().optional(),
    data: z.array(mapPathSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: mapPanelInitParamsSchema.optional(),
  }),
  z.object({
    type: z.literal(PanelType.RawMessage),
    config: z.unknown().optional(),
    data: z.array(topicDataSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Image),
    config: imagePanelConfigSchema.optional(),
    data: z.array(imageClipSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Log),
    config: logPanelConfigSchema.optional(),
    data: z.array(topicDataSchema),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
  z.object({
    type: z.literal(PanelType.Document),
    config: z.unknown().optional(),
    data: documentPanelDataSchema.nullable(),
    id: z.string(),
    title: z.string().optional(),
    initParams: z.unknown().optional(),
  }),
]);

export type PanelState = z.infer<typeof panelSchema>;

export type PlotPanelState = Extract<PanelState, { type: PanelType.Plot }>;
export type LogPanelState = Extract<PanelState, { type: PanelType.Log }>;
export type RawMessagePanelState = Extract<
  PanelState,
  { type: PanelType.RawMessage }
>;
export type ImagePanelState = Extract<PanelState, { type: PanelType.Image }>;
export type MapPanelState = Extract<PanelState, { type: PanelType.Map }>;
export type ThreeDPanelState = Extract<PanelState, { type: PanelType.ThreeD }>;
export type DocumentPanelState = Extract<
  PanelState,
  { type: PanelType.Document }
>;

const layoutItemSchema = z.object({
  id: z.string(),
  relativeSize: z.number(),
  isResizing: z.boolean(),
  type: z.literal(LayoutType.Panel),
});

export type LayoutItem = z.infer<typeof layoutItemSchema>;

const layoutSchema = z.object({
  id: z.string(),
  axis: z.enum(["x", "y"] as const),
  relativeSize: z.number(),
  get children() {
    return z.array(z.union([layoutSchema, layoutItemSchema]));
  },
});

export type Layout = z.infer<typeof layoutSchema>;

export function isLayoutItem(
  layout: Layout | LayoutItem,
): layout is LayoutItem {
  return "children" in layout === false;
}

const eventConfigSchema = z.object({
  isVisible: z.boolean(),
});

export type EventConfig = z.infer<typeof eventConfigSchema>;

/**
 * How the workspace renders a timestamp. Three independent switches consume
 * this enum and each has to be updated together: `formatTimeByMode`
 * (`WorkspaceCtx/TimeDisplayMode.ts`) for the timeline, log rows and tooltips;
 * `PlotRenderer.ts`'s own switch for plot axis ticks; and
 * `getTimeColumnHeader` (`LogPanel/table/TableHeader.tsx`) for the log time
 * column label. Adding a member also means `TIME_DISPLAY_MODES`, which is a
 * hand-listed array rather than a checked mapping, so a mode omitted from it
 * parses and renders correctly while never appearing in the picker.
 *
 * - `Elapsed`: seconds since the workspace's earliest time, to nanosecond
 *   precision (e.g. "0.000000000" at the start).
 * - `ElapsedTime`: that same offset as hh:mm:ss with nine fractional digits;
 *   falls back to `Elapsed` where `Intl.DurationFormat` is unavailable.
 * - `Log`: raw epoch time as seconds.nanoseconds.
 * - `Local`: local timestamp with time zone (e.g. "2024-01-15 10:30:45.123 AM
 *   PST").
 * - `UTC`: ISO-8601 UTC timestamp (e.g. "2024-01-15T18:30:45.123000000Z").
 *
 * Defined here rather than in `WorkspaceCtx` because the enum's string values
 * are persisted vocabulary: they appear in stored workspace configs via
 * `settings.timeDisplayMode`, like `PanelType` and `TimeBoundsSource` above.
 *
 * The values are identifiers, not labels, and they are deliberately none of
 * the strings the UI displays — those live in `TIME_DISPLAY_MODE_LABELS`
 * and is free to reword them, while a stored config keeps resolving. They also
 * name no precision: `ElapsedHms` renders nine fractional digits today, and a
 * value spelling a digit count would have to be migrated to change it. Renaming
 * any of them orphans every config already written, so `v1.test.ts` pins all
 * five literals.
 */
export enum TimeDisplayMode {
  Elapsed = "ElapsedSeconds",
  ElapsedTime = "ElapsedHms",
  Log = "Log",
  Local = "Local",
  UTC = "UTC",
}

export const DEFAULT_TIME_DISPLAY_MODE = TimeDisplayMode.Local;

const eventVisibilitySchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("all") }),
  z.object({ mode: z.literal("none") }),
  /**
   * Show only the events whose names are listed. Names are picked from the
   * workspace's own events, stored verbatim, and matched exactly; an empty
   * list shows everything (fail-open — an unfinished rule must never blank
   * the event track on a link the viewer cannot edit).
   */
  z.object({ mode: z.literal("named"), names: z.array(z.string()) }),
]);

/**
 * Workspace-level event visibility rule. Per-event entries in `State.events`
 * are explicit user overrides and outrank this rule; an event with neither an
 * override nor a rule resolves visible.
 */
export type EventVisibility = z.infer<typeof eventVisibilitySchema>;

/**
 * The settings epoch this client writes; see `settingsSchema.version` below
 * for when to bump it. Not to be confused with {@link VERSION}, which names
 * the config schema as a whole.
 */
export const SETTINGS_VERSION = 1;

/**
 * Workspace-level settings, recorded into the shared config at Share time.
 *
 * Every field carries `.catch(undefined)` so a value this schema rejects
 * degrades that one setting to its system default instead of the whole
 * workspace failing to open (same stance as `xAxisAlignment` above). Absent
 * fields resolve to system defaults: version → 1, events → all visible,
 * timeDisplayMode → `DEFAULT_TIME_DISPLAY_MODE`, layoutId → unset.
 */
export const settingsSchema = z.object({
  /**
   * The settings epoch this block was written under. Absent means 1
   * (`IMPLICIT_SETTINGS_VERSION` in `settings.ts`): configs minted before this
   * field existed are v1 by definition, so no backfill is needed.
   *
   * Nothing branches on the value today. Additions, removals and per-field
   * shape changes are already handled without it: unknown keys survive a round
   * trip through an older client (see `seedSettingsForRestoredWorkspace`), and
   * a rejected field degrades to its system default. The marker covers the one
   * case those cannot — a field whose shape survives an epoch but whose
   * meaning does not.
   *
   * Bump {@link SETTINGS_VERSION} when a change would make an older client
   * misread a block rather than merely miss part of it. Decide the read-side
   * rule then, once v2's shape is known. Do not gate the whole block on
   * version equality: that discards fields whose meaning never changed,
   * inverting this schema's per-field fail-open stance.
   */
  version: z.number().int().optional().catch(undefined),
  events: eventVisibilitySchema.optional().catch(undefined),
  timeDisplayMode: z.enum(TimeDisplayMode).optional().catch(undefined),
  /**
   * The `layout_id` of the layout the sharer had active — a display label,
   * not a directive: the bound panels in `State` always win at open.
   */
  layoutId: z.string().nullable().optional().catch(undefined),
});

export type WorkspaceSettings = z.infer<typeof settingsSchema>;

export const VERSION = "visualization_v1";

export const schema = z.object({
  version: z.literal(VERSION).optional(),
  files: z.array(fileSchema),
  datasources: z.array(datasourceSchema).optional(),
  panels: z.record(z.string(), panelSchema).optional(),
  layout: layoutSchema,
  events: z.record(z.string(), eventConfigSchema).optional(),
  settings: settingsSchema.optional().catch(undefined),
});

export type State = z.infer<typeof schema>;

/**
 * Make a default decision for how to visualize a message path.
 * E.g., a numeric type should be visualized as a plot,
 * a string should be visualized as a "raw message," etc.
 *
 * Takes the whole record rather than its canonical type because `Array` alone
 * does not say what a path holds: a plottable `float64[7]` and a point cloud's
 * `uint8[]` payload share it, and the element type that tells them apart is
 * recoverable only from the source-schema spelling in `data_type` (see
 * {@link arrayDefaultsToPlot}).
 */
export function determineDefaultPanelType(
  messagePath: MessagePathRecord,
): PanelType {
  switch (messagePath.canonical_data_type) {
    case CanonicalDataType.Number:
    case CanonicalDataType.NumberArray:
    case CanonicalDataType.Boolean: // Fall through
      return PanelType.Plot;
    case CanonicalDataType.Array:
      // An array of numbers plots as one series that draws a line per index —
      // all of them in that series' single colour, under one legend row, so
      // the indices are not individually identifiable. Anything else an Array
      // can hold reads better as the decoded message.
      return arrayDefaultsToPlot(messagePath)
        ? PanelType.Plot
        : PanelType.RawMessage;
    case CanonicalDataType.Image:
      return PanelType.Image;
    case CanonicalDataType.Categorical:
    case CanonicalDataType.String:
    case CanonicalDataType.Timestamp: // Fall through
      return PanelType.Log;
    case CanonicalDataType.LatDegFloat:
    case CanonicalDataType.LonDegFloat:
    case CanonicalDataType.LatDegInt:
    case CanonicalDataType.LonDegInt: // Fall through
      return PanelType.Map;
    default:
      return PanelType.RawMessage;
  }
}
